import {
  ApiChartAttribute,
  ApiChartRankDrection,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import { useMemo } from "react";
import { ExploreConfig, TExploreConfig } from "features/explorer/types";
import { ContentExplorer, ExplorerProvider } from "features/explorer";
import { metricsByAttribution } from "features/analytics/services/attributionMapping";
import { AttributionMetric } from "features/analytics";

const keywordSources = ["google", "bing"];
type Tab =
  | "all"
  | "scale"
  | "expensive"
  | "potential"
  | "engaging"
  | "poor"
  | "disconnect";

function KeywordInsights({ paidChannelAttributes }) {
  const attributes: ApiChartAttribute[] = useMemo(() => {
    return [
      ...paidChannelAttributes,
      ...keywordSources.map((source) => ({
        key: ApiDimension.DIMENSION_SOURCE,
        operator: ApiOperator.EQUAL,
        value: source,
      })),
    ];
  }, [paidChannelAttributes]);

  const analyseBy: ExploreConfig["analyseBy"] = useMemo(
    () => ({
      description: "",
      grouping: [
        {
          option: "Keyword",
          dimensions: [ApiDimension.DIMENSION_TERM],
        },
      ],
    }),
    [],
  );

  const config: TExploreConfig<Tab> = useMemo(
    () => ({
      all: {
        title: "All Keywords",
        desc: `Explore all keywords to drive better results.`,
        ranking: [],
        analyseBy,
        attributes,
      },
      scale: {
        title: "Scale Opportunities",
        desc: `High ROAS/conversion with relatively low spend — candidates to increase budget.`,
        default: [
          ApiMetric.METRIC_ROAS,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
          ApiMetric.METRIC_SPEND,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_ROAS,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SPEND,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
            ],
          },
        ],
        analyseBy,
        attributes,
      },
      expensive: {
        title: "Expensive Underperformers",
        desc: `High spend with low ROAS/conversion — candidates to cut back or rework.`,
        default: [
          ApiMetric.METRIC_SPEND,
          ApiMetric.METRIC_ROAS,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_SPEND,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_ROAS,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
            ],
          },
        ],
        analyseBy,
        attributes,
      },
      potential: {
        title: "Potential Pausers",
        desc: `High clicks/sessions but no conversions — strong pause or rework candidates.`,
        default: [
          ApiMetric.METRIC_PUBLISHER_CLICKS,
          ApiMetric.METRIC_SESSIONS_COUNT,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_PUBLISHER_CLICKS,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSIONS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
            ],
          },
        ],
        having: {
          description: "Only keywords with no conversions.",
          havings: [
            {
              label: "Max Conversions",
              metric: ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: 0,
            },
          ],
        },
        analyseBy,
        attributes,
      },
      engaging: {
        title: "Engaging, Not Converting",
        desc: `High engagement (time / effective session rate) but low conversion — intent is there, landing/offer may not be.`,
        default: [
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
            ],
          },
        ],
        analyseBy,
        attributes,
      },
      poor: {
        title: "Poor Engagement, Still Converting",
        desc: `Low engagement but high conversion — converting despite weak on-site experience.`,
        default: [
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
            ],
          },
        ],
        analyseBy,
        attributes,
      },
      disconnect: {
        title: "Publisher–Site Disconnect",
        desc: `High publisher clicks with unusually low tracked sessions / effective session rate — tracking or landing issues.`,
        default: [
          ApiMetric.METRIC_PUBLISHER_CLICKS,
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
        ],
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_PUBLISHER_CLICKS,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSIONS_COUNT,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
            ],
          },
        ],
        analyseBy,
        attributes,
      },
    }),
    [attributes, analyseBy],
  );

  const metrics = useMemo(() => {
    return [
      ApiMetric.METRIC_SPEND,
      ApiMetric.METRIC_PUBLISHER_CPC,
      ApiMetric.METRIC_PUBLISHER_CLICKS,
      ApiMetric.METRIC_SESSIONS_COUNT,
      ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
      ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE,
      ApiMetric.METRIC_REVENUE,
      ApiMetric.METRIC_ROAS,
      ApiMetric.METRIC_RANK,
    ];
  }, []);

  const tableCustomHeaderRender = useMemo(() => {
    return {
      [ApiDimension.DIMENSION_TERM]: () => <div>Keyword</div>,
    };
  }, []);

  const groupedMetrics = useMemo(() => {
    return [
      {
        name: "Publisher Data",
        metrics: [
          ApiMetric.METRIC_SPEND,
          ApiMetric.METRIC_PUBLISHER_CPC,
          ApiMetric.METRIC_PUBLISHER_CLICKS,
        ],
      },
      {
        name: "Traffic & Engagement",
        metrics: [
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
        ],
      },
      {
        name: "Attribution & Conversion",
        metrics: [
          ...metricsByAttribution(AttributionMetric.MACRO),
          ...metricsByAttribution(AttributionMetric.MACRO_RATE),
          ...metricsByAttribution(AttributionMetric.REVENUE),
          ...metricsByAttribution(AttributionMetric.ROAS),
        ],
      },
    ];
  }, []);

  return (
    <ExplorerProvider configs={config} type="all">
      <ContentExplorer
        groupedMetrics={groupedMetrics}
        dontIncludeEmpty
        dimensionNoClick
        tableCustomHeaderRender={tableCustomHeaderRender}
        metrics={metrics}
      />
    </ExplorerProvider>
  );
}

export default withPaidChannelAttributes(KeywordInsights);
